import { memo, type ReactNode, useMemo } from 'react';
import { merge } from 'lodash';
import styled from 'styled-components';
import { useFeatureFlagEnabled } from '@frameio/feature-flags';
import { getDisplayCategoryForAsset } from '@frameio/graphql-client';
import { plural } from '@frameio/i18n';
import { useVersionStackModalContext } from '@frameio/shared/contexts';
import type { MediaThumbnailProps } from '@frameio/vapor';
import {
  Box,
  CommentCountLabel,
  MediaThumbnail,
  TimecodeLabel,
  VersionNumberLabel,
} from '@frameio/vapor';
import {
  AssetLifecycleExpirationLabel,
  canScrubAsset,
  getDuration,
  getThumbMedia,
  getThumbScrub,
  PermanentDeletionLabel,
  useAssetAggregateLayout,
  useCardAppearance,
  useLinkedSequencesForAsset,
  useThumbnailContainerDimensions,
} from '../../../shared';
import { useAssetThumbnailFragment } from '../../../shared/gql/generated/AssetThumbnail';
import { useAssetThumbScrubFragment } from '../../../shared/gql/generated/AssetThumbScrub';
import {
  AssetStateIndicator,
  TimecodeReveal,
  useAssetState,
} from '../../../states';
import { InteractiveLabel, SequenceIcon } from './components';
import TEXT from './generated/TEXT/Thumb';

export type DisableAffordancesConfig = {
  /** Disable comment count label */
  commentCount?: boolean;
  /** Force disable the deletedAt warning */
  deletedAt?: boolean;
  /** Force disable the asset lifecycle expiration label */
  expiration?: boolean;
  /** Force disable the scrubber for video thumbs */
  scrub?: boolean;
  /** Disable the timecode label for video assets */
  timecode?: boolean;
  /** Disable display of the version stack number */
  versionNumber?: boolean;
  /** Disable toggling of the version stack modal */
  versionStackModal?: boolean;
};

export type RenderThumbProps = {
  /** The asset being rendered in this Thumb */
  assetId: string;
  /** Set of affordances that can be prevented from rendering */
  disableAffordances?: DisableAffordancesConfig;
  /** The version number for this Thumb */
  version?: number;
  /** Override the image fit */
  isImageFitPreferred?: boolean | undefined;
  /** The prefix label to display before the version number */
  versionPrefixLabel?: ReactNode;
} & Partial<
  Pick<MediaThumbnailProps, 'isThumbnailFadeInEnabled' | 'borderRadius'>
>;

const DEFAULT_AFFORDANCES: DisableAffordancesConfig = {
  commentCount: false,
  deletedAt: true,
  expiration: false,
  scrub: false,
  timecode: false,
  versionNumber: false,
  versionStackModal: false,
};

/**
 * The Thumb component wraps the MediaThumbnail for an Asset with the TransferIndicator
 * component that renders the different transfer states. It should be passed to
 * the VersionStackThumbnailAnimator component so that multiple Thumbs can be
 * rendered during the version stack animation.
 *
 * @see VersionStackThumbnailAnimator
 */
export const Thumb = memo(
  ({
    assetId,
    disableAffordances: disableAffordancesInput,
    isThumbnailFadeInEnabled,
    borderRadius,
    isImageFitPreferred,
    versionPrefixLabel = TEXT.versionPrefixLabel,
  }: RenderThumbProps) => {
    const { data: asset, complete: assetComplete } = useAssetThumbnailFragment({
      from: {
        id: assetId,
        __typename: 'Asset',
      },
    });

    const { sequences } = useLinkedSequencesForAsset({ assetId });

    const disableAffordances = {
      ...DEFAULT_AFFORDANCES,
      ...disableAffordancesInput,
    };

    const appearance = useCardAppearance();
    const isList = useAssetAggregateLayout() === 'LIST';
    const { toggleVersionStackModal } = useVersionStackModalContext();
    const neutralBackground = useFeatureFlagEnabled(
      'v4.enhanced_media_contrast'
    );

    const { data: scrubData, complete: scrubComplete } =
      useAssetThumbScrubFragment({
        from: asset?.id ? asset : { __typename: '', id: '' },
      });

    const mediaScrub =
      scrubComplete && 'media' in scrubData && scrubData.media
        ? scrubData
        : undefined;

    const canScrub = mediaScrub
      ? canScrubAsset(mediaScrub) && !disableAffordances?.scrub
      : false;

    const media = useMemo(
      () =>
        merge(
          asset && assetComplete ? getThumbMedia(asset) : {},
          mediaScrub ? getThumbScrub(mediaScrub) : {}
        ),
      [asset, assetComplete, mediaScrub]
    );

    const containerDimensions = useThumbnailContainerDimensions();

    /**
     * make sure we have the complete fragment before rendering the MediaThumbnail.
     * MediaThumbnail.media requires an object, but the urls can be '', at which
     * point it will render a fallback icon. problem is we don't know if a fallback
     * icon is required (i.e. it's missing a thumbnail) until after the fragment
     * is complete.
     */
    const isThumbnailFragmentIncomplete =
      !assetComplete && !media.thumbs?.thumb2;
    /**
     * if the asset is in the process of converting to an interactive asset,
     * we want to skip the blank thumb state so we can see the fallback icon in its
     * processing state.
     */
    const isConvertingToInteractive = useAssetState() === 'interactive';

    if (
      !asset ||
      (isThumbnailFragmentIncomplete && !isConvertingToInteractive)
    ) {
      return null;
    }

    const assetDuration = assetComplete ? getDuration(asset) : '';

    const displayCategory = assetComplete
      ? getDisplayCategoryForAsset(asset)
      : undefined;

    const handleVersionNumberClick = () => {
      if (asset.parent && !disableAffordances?.versionStackModal) {
        toggleVersionStackModal(asset.parent.id);
      }
    };

    const hasInteractiveLabel =
      asset.__typename === 'InteractiveAsset' ||
      asset.__typename === 'UnsupportedAsset' ||
      asset.__typename === 'InteractiveLiveAsset';

    const isModelAsset = asset.__typename === 'ModelAsset';

    return (
      <AssetStateIndicator size={isList ? 'xsmall-compact' : 'medium'}>
        <Container $neutralBackground={neutralBackground}>
          <MediaThumbnail
            assetTypename={asset.__typename ?? ''}
            borderRadius={borderRadius}
            contentBottomLeft={
              !isList && (
                <>
                  {!disableAffordances?.expiration && asset.archiveFrom && (
                    <AssetLifecycleExpirationLabel
                      archiveFrom={asset.archiveFrom}
                    />
                  )}
                  {!disableAffordances?.deletedAt && asset.deletedAt && (
                    <PermanentDeletionLabel deletedAt={asset.deletedAt} />
                  )}
                  {asset.commentCount && !disableAffordances?.commentCount ? (
                    <CommentCountLabel
                      aria-label={TEXT.commentCountLabel({
                        count: asset.commentCount,
                      })}
                      count={asset.commentCount}
                    />
                  ) : undefined}
                </>
              )
            }
            contentBottomRight={
              <>
                {!isList && hasInteractiveLabel ? (
                  <InteractiveLabel
                    isInteractive={asset.__typename === 'InteractiveAsset'}
                    isInteractiveEligible={
                      asset.__typename === 'UnsupportedAsset' &&
                      asset.isInteractiveEligible
                    }
                    isWeb={asset.__typename === 'InteractiveLiveAsset'}
                  />
                ) : undefined}
                {!isList &&
                !hasInteractiveLabel &&
                !isModelAsset &&
                !disableAffordances?.timecode ? (
                  <TimecodeReveal>
                    {assetDuration && (
                      <TimecodeLabel>{assetDuration}</TimecodeLabel>
                    )}
                  </TimecodeReveal>
                ) : undefined}
              </>
            }
            contentTopRight={
              <Box alignItems="center" display="flex" gap={2}>
                {Boolean(
                  !isList &&
                    asset.versionNumber &&
                    !disableAffordances?.versionNumber
                ) && (
                  <VersionNumberLabel
                    version={asset.versionNumber}
                    versionPrefixLabel={versionPrefixLabel}
                    {...(!disableAffordances?.versionStackModal && {
                      onClick: handleVersionNumberClick,
                    })}
                  />
                )}
                {!isList && sequences.length > 0 && (
                  <SequenceIcon
                    isLinkedToMultipleSequences={sequences.length > 1}
                    sequenceName={sequences[0]?.sequenceName}
                  />
                )}
              </Box>
            }
            data-chromatic="ignore"
            displayCategory={displayCategory}
            height={containerDimensions.height || undefined}
            isIconFallbackBackgroundVisible={false}
            isImageFitPreferred={
              isImageFitPreferred !== undefined
                ? isImageFitPreferred
                : Boolean(appearance?.cardFitImage)
            }
            isScrubEnabled={canScrub}
            isThumbnailFadeInEnabled={isThumbnailFadeInEnabled}
            media={media}
            role="figure"
            scrubBarWidth={2}
            width={containerDimensions.width || undefined}
          />
        </Container>
      </AssetStateIndicator>
    );
  }
);

Thumb.displayName = 'Thumb';

const Container = styled.div<{ $neutralBackground: boolean }>`
  height: 100%;
  overflow: hidden;
  width: 100%;

  ${(p) =>
    p.$neutralBackground &&
    `& img, & div:has(+ img) {
      background-color: var(--palette-grey-neutral-15);
    }`}
`;

TEXT.DEFINE({
  /** The prefix label to display before the version number */
  versionPrefixLabel: 'V',
  /** Accessible label for the comment count indicator (e.g. "5 comments"). */
  commentCountLabel: ({ count }: { count: number }): string =>
    plural(count, `${count} comments`, '1 comment'),
});
